import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "../api";
import { useSite } from "../context/SiteContext";
import { MaskedLines, Reveal, Counter, ChapterHeading } from "../components/motion";
import LivePulse from "../components/LivePulse";
import BoosterStrip from "../components/BoosterStrip";
import Marquee from "../components/Marquee";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Avatar, AvatarFallback } from "../components/ui/avatar";

const AVATAR_COLORS = ["#5865F2", "#06B6D4", "#10B981", "#F59E0B", "#EF4444", "#EC4899"];

function Stars({ rating, size = 16 }) {
  return (
    <div className="flex gap-0.5" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((i) => (
        <Icons.Star key={i} size={size} className={i <= rating ? "text-amber-400" : "text-white/15"} fill={i <= rating ? "#F59E0B" : "none"} />
      ))}
    </div>
  );
}

function ReviewForm() {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/public/reviews", { username, rating, text });
      toast.success(res.data.message);
      setOpen(false);
      setUsername(""); setText(""); setRating(5);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit review");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="write-review-button" className="inline-flex items-center gap-2 border border-blurple/50 text-blurple hover:bg-blurple/10 font-semibold text-sm px-6 py-3 rounded-full transition-colors duration-200">
          <Icons.PenLine size={15} /> Write a review
        </button>
      </DialogTrigger>
      <DialogContent className="bg-panel border-white/10 text-white" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Review GrooveMusic</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-white/50">Discord username</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={2}
              maxLength={32}
              data-testid="review-username-input"
              className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blurple"
              placeholder="e.g. Spazzy"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-white/50">Rating</label>
            <div className="flex gap-1.5 mt-1.5" data-testid="review-rating-input">
              {[1, 2, 3, 4, 5].map((i) => (
                <button type="button" key={i} onClick={() => setRating(i)} aria-label={`${i} stars`}>
                  <Icons.Star size={26} className={i <= rating ? "text-amber-400" : "text-white/15 hover:text-amber-400/50"} fill={i <= rating ? "#F59E0B" : "none"} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-white/50">Your review</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              required
              minLength={10}
              maxLength={500}
              rows={4}
              data-testid="review-text-input"
              className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blurple resize-none"
              placeholder="What did GrooveMusic get right?"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="review-submit-button"
            className="w-full bg-blurple hover:bg-[#4752c4] disabled:opacity-50 text-white font-semibold py-3 rounded-full transition-colors duration-200"
          >
            {busy ? "Submitting…" : "Submit for moderation"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Home() {
  const { site, stats, features, faqs } = useSite();
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/public/reviews").then((r) => setReviews(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const heroLines = site?.hero_lines || ["YOUR MUSIC.", "YOUR SERVER.", "ONE BOT."];
  const invite = site?.bot_invite_url || "#";
  const heroFeatures = useMemo(() => features.slice(0, 6), [features]);
  const statItems = useMemo(() => stats ? [
    { label: "Servers", value: stats.servers, icon: Icons.Server },
    { label: "Users", value: stats.users, icon: Icons.Users },
    { label: "Songs played", value: stats.songs_played, icon: Icons.Music2 },
    { label: "Uptime", value: stats.uptime, suffix: "%", decimals: 1, icon: Icons.Activity },
  ] : [], [stats]);

  return (
    <main data-testid="home-page">
      {site?.announcement_banner?.enabled && (
        <div className="fixed top-16 inset-x-0 z-40 bg-blurple/15 border-b border-blurple/30 backdrop-blur-md" data-testid="announcement-banner">
          <p className="text-center text-xs sm:text-sm text-white/85 py-2 px-4 font-mono">
            <Icons.Sparkles size={13} className="inline mr-2 text-volt" />
            {site.announcement_banner.text}
          </p>
        </div>
      )}

      {/* HERO */}
      <section className="relative min-h-screen flex items-center hero-glow overflow-hidden pt-24" data-testid="hero-section">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none">
          <img src="https://images.unsplash.com/photo-1736843638421-9c3770d28c91?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBtdXNpYyUyMHdhdmV8ZW58MHx8fHwxNzg4MDk3MTA4fDA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-16 items-center w-full">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-xs uppercase tracking-[0.35em] text-volt font-semibold mb-8 flex items-center gap-3"
              data-testid="hero-overline"
            >
              <span className="w-8 h-px bg-volt inline-block" />
              {site?.hero_overline || "The Discord music bot"}
            </motion.p>
            <h1 className="font-display font-900 leading-[0.98] tracking-tight" style={{ fontWeight: 900 }} data-testid="hero-headline">
              <MaskedLines
                lines={heroLines}
                lineClassName="text-4xl sm:text-5xl lg:text-6xl text-white last:text-outline-blurple"
              />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.7 }}
              className="text-muted-foreground text-base md:text-lg mt-8 max-w-lg leading-relaxed"
              data-testid="hero-subtitle"
            >
              {site?.hero_subtitle}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.7 }}
              className="flex flex-wrap gap-4 mt-10"
            >
              <a
                href={invite}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-add-to-discord"
                className="inline-flex items-center gap-2.5 bg-blurple hover:bg-[#4752c4] text-white font-heading font-bold px-8 py-4 rounded-full transition-colors duration-200 shadow-[0_0_40px_rgba(88,101,242,0.5)]"
              >
                <Icons.Plus size={18} strokeWidth={3} /> Add to Discord
              </a>
              <Link
                to="/commands"
                data-testid="hero-view-commands"
                className="inline-flex items-center gap-2.5 border border-white/20 hover:border-white/50 hover:bg-white/5 text-white font-heading font-semibold px-8 py-4 rounded-full transition-colors duration-200"
              >
                <Icons.Terminal size={17} /> View Commands
              </Link>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="flex justify-center lg:justify-end"
          >
            <LivePulse />
          </motion.div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/30 animate-bounce">
          <Icons.ChevronDown size={22} />
        </div>
      </section>

      <Marquee />

      <BoosterStrip />

      {/* IN-DISCORD SHOWCASE */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-28" data-testid="showcase-section">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-volt font-semibold mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-volt inline-block" /> In the wild
            </p>
            <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight leading-tight" style={{ fontWeight: 800 }}>
              This is GrooveMusic,<br />inside your server.
            </h2>
            <p className="text-muted-foreground mt-6 leading-relaxed max-w-md">
              One command posts a rich player card — album art, progress, requester and buttons anyone can tap. No memorising syntax, no alt-tabbing.
            </p>
            <div className="mt-8 rounded-xl overflow-hidden border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
              <img src="/assets/banner.jpg" alt="GrooveMusic brand banner" className="w-full object-cover" data-testid="brand-banner" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl bg-[#313338] border border-white/10 overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.55)]" data-testid="discord-mock">
              <div className="bg-[#1e1f22] px-4 py-3 flex items-center gap-2 border-b border-black/40">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#80848e" strokeWidth="2"><path d="M4 9h16M4 15h16M10 3L8 21M16 3l-2 18"/></svg>
                <span className="text-sm font-semibold text-white/90">music</span>
                <span className="ml-auto text-xs text-[#949ba4] hidden sm:inline">GrooveMusic · v2.4.0</span>
              </div>
              <div className="p-5 space-y-6">
                <div className="flex gap-3.5">
                  <div className="w-10 h-10 rounded-full bg-volt flex items-center justify-center text-void font-bold text-sm shrink-0">SP</div>
                  <div>
                    <p className="text-sm"><span className="font-semibold text-white">Spazzy</span> <span className="ml-2 text-xs text-[#949ba4]">Today at 9:41 PM</span></p>
                    <p className="text-sm text-[#dbdee1] mt-0.5 font-mono">!play midnight city</p>
                  </div>
                </div>
                <div className="flex gap-3.5">
                  <img src="/assets/logo.jpg" alt="GrooveMusic" className="w-10 h-10 rounded-full object-cover shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm">
                      <span className="font-semibold text-white">GrooveMusic</span>
                      <span className="ml-2 text-[10px] font-bold bg-blurple text-white rounded px-1.5 py-0.5 align-middle">APP</span>
                      <span className="ml-2 text-xs text-[#949ba4]">Today at 9:41 PM</span>
                    </p>
                    <div className="mt-2.5 bg-[#2b2d31] border-l-4 border-blurple rounded-md p-4 max-w-md">
                      <p className="text-xs text-[#949ba4] font-semibold uppercase tracking-wider mb-1.5">Now playing</p>
                      <p className="text-sm font-bold text-white">Midnight City — M83</p>
                      <div className="mt-3 h-1.5 rounded-full bg-black/40 overflow-hidden">
                        <div className="h-full w-2/5 bg-blurple rounded-full" />
                      </div>
                      <p className="text-[11px] font-mono text-[#949ba4] mt-2">1:37 / 4:03 · requested by Spazzy · 320kbps</p>
                      <div className="flex gap-2 mt-3.5 flex-wrap">
                        {["Pause", "Skip", "Loop", "Shuffle", "Stop"].map((b) => (
                          <span key={b} className={`text-xs font-semibold rounded px-3 py-1.5 ${b === "Pause" ? "bg-blurple text-white" : "bg-[#1e1f22] text-[#dbdee1]"}`}>{b}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 01 FEATURES */}
      {site?.sections?.features !== false && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-28" data-testid="features-section">
          <ChapterHeading number="01" title="Everything you need for music." sub="Fifteen deeply-engineered capabilities. Zero bloat. Every one of them free." />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {heroFeatures.map((f, i) => {
              const Icon = Icons[f.icon] || Icons.Music;
              return (
                <Reveal key={f.id} delay={i * 0.06} className={f.span === "wide" ? "md:col-span-8" : "md:col-span-4"}>
                  <div className="group h-full card-beam rounded-xl bg-white/[0.03] border border-white/10 p-8 hover:-translate-y-1 hover:border-blurple/40 transition-[transform,border-color] duration-300" data-testid={`feature-card-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="w-12 h-12 rounded-lg bg-blurple/15 border border-blurple/25 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <Icon size={22} className="text-blurple" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-white mb-2">{f.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-10 text-center">
            <Link to="/features" data-testid="features-see-all" className="inline-flex items-center gap-2 text-volt hover:text-white font-semibold text-sm transition-colors duration-200">
              See all 15 features <Icons.ArrowRight size={15} />
            </Link>
          </Reveal>
        </section>
      )}

      {/* 02 STATS */}
      {site?.sections?.stats !== false && stats && (
        <section className="border-y border-white/10 bg-[#07070c] scanlines" data-testid="stats-section">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
            <ChapterHeading number="02" title="Numbers that speak." sub="Live platform statistics, updated by the GrooveMusic team." />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {statItems.map((s, i) => (
                <Reveal key={s.label} delay={i * 0.08}>
                  <div className="rounded-xl border border-white/10 bg-panel p-8 relative overflow-hidden" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <s.icon size={20} className="text-blurple mb-6" />
                    <p className="font-display text-3xl sm:text-4xl text-white" style={{ fontWeight: 800 }}>
                      <Counter value={s.value} suffix={s.suffix || ""} decimals={s.decimals || 0} />
                    </p>
                    <p className="text-xs uppercase tracking-[0.2em] text-white/40 mt-2 flex items-center gap-2">
                      {s.label}
                      {s.label === "Uptime" && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 03 REVIEWS */}
      {site?.sections?.reviews !== false && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-28" data-testid="reviews-section">
          <ChapterHeading number="03" title="Loved by loud servers." sub="Real reviews from real Discord communities. Yours could be next." />
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
            {reviews.map((r, i) => (
              <Reveal key={r.id} delay={(i % 3) * 0.08} className="break-inside-avoid">
                <div className={`rounded-xl border p-7 ${r.featured ? "card-beam bg-blurple/[0.06] border-blurple/30" : "bg-white/[0.03] border-white/10"}`} data-testid={`review-${r.username}`}>
                  <div className="flex items-center justify-between mb-4">
                    <Stars rating={r.rating} />
                    {r.featured && <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-volt border border-volt/30 rounded-full px-2.5 py-1">Featured</span>}
                  </div>
                  <p className="text-white/90 leading-relaxed text-[15px]">“{r.text}”</p>
                  <div className="flex items-center gap-3 mt-6">
                    <Avatar className="w-9 h-9">
                      <AvatarFallback style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }} className="text-white text-xs font-bold">
                        {r.username.slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-semibold text-white">{r.username}</p>
                      <p className="text-xs text-white/40">Discord user</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 text-center">
            <ReviewForm />
          </Reveal>
        </section>
      )}

      {/* 04 FAQ */}
      {site?.sections?.faq !== false && faqs.length > 0 && (
        <section className="border-t border-white/10 bg-[#07070c]" data-testid="faq-section">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 py-28">
            <ChapterHeading number="04" title="Questions, answered." />
            <Reveal>
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((f, i) => (
                  <AccordionItem key={f.id} value={`faq-${i}`} className="border border-white/10 rounded-xl bg-white/[0.03] px-6 data-[state=open]:border-blurple/40" data-testid={`faq-item-${i}`}>
                    <AccordionTrigger className="text-left font-heading font-semibold text-white hover:no-underline py-5 text-base">
                      {f.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed pb-6 text-sm">
                      {f.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-32">
          <Reveal className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl sm:text-5xl text-white leading-tight" style={{ fontWeight: 800 }}>
              Ready to upgrade your<br /><span className="text-outline-blurple">server's music?</span>
            </h2>
            <a
              href={invite}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-add-groovemusic"
              className="mt-10 inline-flex items-center gap-2.5 bg-blurple hover:bg-[#4752c4] text-white font-heading font-bold text-lg px-10 py-5 rounded-full transition-colors duration-200 shadow-[0_0_60px_rgba(88,101,242,0.55)]"
            >
              <Icons.Plus size={20} strokeWidth={3} /> Add GrooveMusic
            </a>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-20 max-w-4xl mx-auto">
            {["Click Add to Discord", "Select your server", "Authorise GrooveMusic", "Start listening"].map((step, i) => (
              <Reveal key={step} delay={i * 0.1}>
                <div className="text-center" data-testid={`cta-step-${i}`}>
                  <p className="font-display text-outline text-3xl mb-3" style={{ fontWeight: 700 }}>{String(i + 1).padStart(2, "0")}</p>
                  <p className="text-sm text-white/70">{step}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
